import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { travelPayrollApi } from '@/lib/apiClient';
import { emailSummary, lastPayWeek, rateSummary, runLabel } from './travelPayrollText';

/**
 * The Travel Acknowledgment payroll report: every acknowledgment SIGNED in a
 * date range, with each rate exactly as signed. The same workbook is emailed
 * to payroll each week once the email is switched on; this panel is for
 * checking a week, downloading it, and re-sending one.
 *
 * ⚠️ RATES, NOT AMOUNTS — the report never multiplies. Per-diem days depend on
 * the days each person actually traveled, worked and stayed out (handbook
 * item 2), which payroll decides.
 *
 * Shown to schedulers (manpower.edit) and to the payroll permission
 * (travel.payroll_report, held by the HR role) — the API enforces the same.
 */

export function TravelPayrollReportPanel() {
  const [range, setRange] = useState(lastPayWeek());
  const [shown, setShown] = useState(range);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const qc = useQueryClient();

  const entries = useQuery({
    queryKey: ['travel-payroll-report', shown.from, shown.to],
    queryFn: () => travelPayrollApi.entries(shown.from, shown.to),
    staleTime: 0,
  });
  const status = useQuery({
    queryKey: ['travel-payroll-email'],
    queryFn: () => travelPayrollApi.emailStatus(),
    staleTime: 0,
  });
  const resend = useMutation({
    mutationFn: () => travelPayrollApi.sendEmail(shown.from, shown.to),
    onSettled: () => qc.invalidateQueries({ queryKey: ['travel-payroll-email'] }),
  });

  async function download() {
    setDownloadError(null);
    try {
      const blob = await travelPayrollApi.workbook(shown.from, shown.to);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `travel-acknowledgments-signed-${shown.from}-to-${shown.to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(`Download failed: ${(err as Error).message}`);
    }
  }

  const small = { fontSize: 12, color: 'var(--ew-n-500)', lineHeight: 1.5 } as const;
  const th: React.CSSProperties = { textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--ew-n-200)' };
  const td: React.CSSProperties = { padding: '4px 8px', borderBottom: '1px solid var(--ew-n-100)', verticalAlign: 'top' };
  const s = status.data;
  const canResend = !!s && !!s.from && s.to.length > 0;

  return (
    <div style={{ fontSize: 13 }}>
      <p style={small}>
        Every Travel Acknowledgment signed in the dates below, with each rate exactly as the traveler
        signed it. These are rates, not amounts: how many days each rate applies to is payroll's call.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '8px 0' }}>
        <label>
          From{' '}
          <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
        </label>
        <label>
          To <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
        </label>
        <button type="button" className="btn btn-secondary" onClick={() => setShown(range)}>
          Show
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            const w = lastPayWeek();
            setRange(w);
            setShown(w);
          }}
        >
          Last pay week
        </button>
        <button type="button" className="btn btn-primary" onClick={download} disabled={!entries.data}>
          Download Excel
        </button>
      </div>
      {downloadError && <p style={{ color: 'var(--ew-danger, #c0392b)' }}>{downloadError}</p>}

      {entries.isLoading && <p style={small}>Loading…</p>}
      {entries.isError && (
        <p style={{ color: 'var(--ew-danger, #c0392b)' }}>{(entries.error as Error).message}</p>
      )}
      {entries.data && (
        entries.data.length === 0 ? (
          <p style={small}>No Travel Acknowledgments were signed {shown.from} to {shown.to}.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={th}>Signed</th>
                <th style={th}>Employee</th>
                <th style={th}>Job</th>
                <th style={th}>Band</th>
                <th style={th}>Rates</th>
              </tr>
            </thead>
            <tbody>
              {entries.data.map((e) => (
                <tr key={e.acknowledgmentId}>
                  <td style={td}>{e.signedDate}</td>
                  <td style={td}>
                    {e.person.name ?? '—'}
                    {e.person.employeeId ? ` (${e.person.employeeId})` : ''}
                  </td>
                  <td style={td}>{[e.job.jobNumber, e.job.title].filter(Boolean).join(' — ') || '—'}</td>
                  <td style={td}>
                    {e.job.travelBand ?? '—'}
                    {e.job.isPrevailingWage ? ' · prevailing wage' : ''}
                  </td>
                  <td style={td}>
                    {rateSummary(e.lines)}
                    {e.supersededOn && (
                      <div style={{ color: 'var(--ew-warning, #b8860b)' }}>
                        Replaced by a later acknowledgment signed {e.supersededOn}
                      </div>
                    )}
                    {e.caveats.map((c) => (
                      <div key={c} style={small}>{c}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {s && (
        <div style={{ marginTop: 14 }}>
          <p style={small}>{emailSummary(s)}</p>
          {s.recent.length > 0 && (
            <ul style={{ ...small, paddingLeft: 18, margin: '4px 0' }}>
              {s.recent.map((r) => {
                const { text, tone } = runLabel(r);
                const color = tone === 'bad' ? 'var(--ew-danger, #c0392b)'
                  : tone === 'warn' ? 'var(--ew-warning, #b8860b)' : undefined;
                return <li key={r.id} style={{ color }}>{text}</li>;
              })}
            </ul>
          )}
          {canResend && (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={resend.isPending}
              onClick={() => {
                if (window.confirm(`Email the report for ${shown.from} to ${shown.to} to payroll now?`)) resend.mutate();
              }}
            >
              {resend.isPending ? 'Sending…' : 'Email these dates to payroll now'}
            </button>
          )}
          {resend.isError && (
            <p style={{ color: 'var(--ew-danger, #c0392b)' }}>{(resend.error as Error).message}</p>
          )}
          {resend.data?.status === 'sent' && <p style={small}>Sent.</p>}
        </div>
      )}
    </div>
  );
}
